import React, { useCallback } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { saveGuildContinueWatching } from '@/services/GuildContinueWatching'
import { useGuildMember } from '@/services/GuildUserService'
import { isStreaming } from '@/services/PhaseManagerService'
import { Episode, Project } from '@/services/ProjectsService'
import { Trailer } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'

interface LoginToWatchButtonGroupProps {
  episode?: Episode | null
  isGuildMemberContent: boolean
  slug: string
  trailer?: Trailer
  project?: Project
  isLoggedIn?: boolean
}

function areDatesEqual(date1?: Date, date2?: Date) {
  if (!date1 || !date2) return false
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}
export const LoginToWatchButtonGroup: React.FC<LoginToWatchButtonGroupProps> = ({
  episode,
  isGuildMemberContent,
  slug,
  trailer,
  project,
  isLoggedIn = false,
}) => {
  const now = new Date()
  const earlyAccessAvailableDate = episode?.earlyAccessDate ? new Date(episode?.earlyAccessDate) : undefined
  const publiclyAvailableDate = episode?.publiclyAvailableDate ? new Date(episode?.publiclyAvailableDate) : undefined
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { isGuildMember } = useGuildMember()
  const track = useSafeTrack()

  const handleJoinToWatchClicked = useCallback(() => {
    track('Clicked Join to Watch', { episode })
    if (episode && isStreaming(project?.primaryFlowPhases ?? [])) saveGuildContinueWatching(episode, track, locale)
  }, [episode, track, locale, project])

  const availableDateTime = areDatesEqual(earlyAccessAvailableDate, new Date())
    ? Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(earlyAccessAvailableDate)
    : earlyAccessAvailableDate?.toLocaleDateString()

  const publiclyAvailableDateTime = areDatesEqual(publiclyAvailableDate, new Date())
    ? Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(publiclyAvailableDate)
    : publiclyAvailableDate?.toLocaleDateString()

  return (
    <div className={classNames('flex flex-col md:flex-row', { 'w-full': !isGuildMemberContent && !isGuildMember })}>
      <When condition={isGuildMemberContent && !isGuildMember}>
        {earlyAccessAvailableDate && earlyAccessAvailableDate <= now ? (
          <LinkButton
            className="box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:w-fit"
            href={`${paths.guild.join}/${slug}`}
            onClick={handleJoinToWatchClicked}
            variant="white"
          >
            <div className="mr-2">
              <GuildIcon size={24} />
            </div>
            <CaptionSM weight="bold" className="text-base">
              {t('joinToWatch', 'Join To Watch')}
            </CaptionSM>
          </LinkButton>
        ) : (
          <Button
            className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
            variant="white"
          >
            <div className="mr-2">
              <GuildIcon size={24} />
            </div>
            <CaptionSM weight="bold" className="text-base">
              {t('availableDate', 'Available {{date}}', { date: availableDateTime })}
            </CaptionSM>
          </Button>
        )}
      </When>
      <When condition={isGuildMemberContent && isGuildMember}>
        {earlyAccessAvailableDate && earlyAccessAvailableDate >= now && (
          <Button
            className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
            variant="white"
          >
            <CaptionSM weight="bold" className="text-base">
              {t('availableDate', 'Available {{date}}', { date: availableDateTime })}
            </CaptionSM>
          </Button>
        )}
      </When>
      <When condition={!isGuildMemberContent && !isGuildMember}>
        {publiclyAvailableDate && publiclyAvailableDate >= now ? (
          <Button
            className="box-border h-8 min-h-[50px] w-full cursor-not-allowed rounded-md backdrop-blur-sm md:w-fit"
            variant="white"
          >
            <CaptionSM weight="bold" className="text-base">
              {t('availableDate', 'Available {{date}}', { date: publiclyAvailableDateTime })}
            </CaptionSM>
          </Button>
        ) : (
          publiclyAvailableDate &&
          publiclyAvailableDate < now && (
            <div className="w-full">
              <GuildEmailInput
                variant="left-white"
                loginOrigin="episode"
                hasUser={isLoggedIn}
                isCentered={false}
                isHome
              />
            </div>
          )
        )}
      </When>
      {Boolean(trailer?.id) && (
        <LinkButton
          href={`/watch/${slug}/videos/trailer/${trailer?.id}`}
          variant="white"
          outline
          className="mt-4 box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:ml-4 md:mt-0 md:w-fit"
        >
          <CaptionSM weight="bold" className="text-base">
            {t('watchTrailer', 'Watch Trailer')}
          </CaptionSM>
        </LinkButton>
      )}
    </div>
  )
}
